import { MutationHookOptions } from '@apollo/client'
import { customerErrorHandling } from 'lib/hooks'
import {
  CustomerType,
  CustomerUpdateInput,
  PrefectureCode,
  useCustomerUpdateMutation,
} from 'lib/graphql/storeadmin'
import { Form, Input, Modal, ModalProps, showMessage } from 'components/common'
import { AddressAndDetailsForm } from 'components/customers'
import AllowDuplicateModal from './AllowDuplicateModal'
import { useState } from 'react'
import {
  DuplicateFieldsValue,
  getDuplicateErrorMessages,
} from 'pages/admin/customers/corporate/[id]/edit'

type FormValues = {
  allowDuplicate?: boolean
  id: string
  address1: string
  address2: string
  prefectureCode: string
  zip: string
  city: string
  type?: string
}

export interface AddressEditorModalProps
  extends Pick<ModalProps, 'visible' | 'onCancel'>,
    Pick<MutationHookOptions, 'refetchQueries'> {
  defaultValues?: FormValues
  type: 'personal' | 'corporate'
  onSuccess?: () => void | Promise<void>
  onErrorMessage?: (value: string[]) => void | Promise<void>
  onErrorAlert?: () => void
  onCancel?: () => void
}

export const AddressEditorModal = ({
  defaultValues,
  type,
  visible,
  refetchQueries,
  onSuccess,
  onCancel,
  onErrorMessage,
  onErrorAlert,
}: AddressEditorModalProps): JSX.Element => {
  const [form] = Form.useForm<FormValues>()
  const [updateCustomer, { loading, error }] = useCustomerUpdateMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  })

  // モーダルの表示・非表示
  const [showModal, setShowModal] = useState(false)
  // 重複可否を選択するモーダルのonOKの引数に渡すvaluesを一時的に保存するためのステート
  const [tmpValues, setTmpValues] = useState<CustomerUpdateInput>(null)
  // 重複エラーのフィールド名を日本語の項目名に変換した文字列を格納するステート
  const [duplicateErrorMessages, setDuplicateErrorMessages] =
    useState<string[]>(null)

  // 重複可否選択モーダルのOKボタンをクリックしたときの処理
  const handleClickAllowDuplicate = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (tmpValues) {
      onFinish({
        allowDuplicate: true,
        id: defaultValues.id,
        prefectureCode: tmpValues.address.prefectureCode,
        zip: tmpValues.address.zip,
        city: tmpValues.address.city,
        address1: tmpValues.address.address1,
        address2: tmpValues.address.address2,
        type,
      })
    } else {
      showMessage('error', 'エラーが発生しました。')
    }
    setShowModal(false)
  }

  const onFinish = async ({
    // 個人の時は重複可否をチェックしない
    allowDuplicate = type === 'personal' ? true : false,
    id,
    address1,
    address2,
    prefectureCode,
    zip,
    city,
  }: FormValues) => {
    const input = {
      address: {
        address1,
        address2,
        prefectureCode: prefectureCode as PrefectureCode,
        zip,
        city,
      },
      type:
        type === 'personal' ? CustomerType.Personal : CustomerType.Corporate,
    }

    try {
      const { data } = await updateCustomer({
        variables: { allowDuplicate, id, input },
      })
      const userErrors = data?.customerUpdate?.customerUserErrors || []
      if (userErrors.length > 0) {
        // 重複エラーがある場合
        if (userErrors.some(({ code }) => code === 'DUPLICATE_DATA_EXISTS')) {
          // 重複エラーがある場合、重複可否を選択するモーダルを表示する
          setShowModal(true)

          // 編集モーダルを閉じる
          onCancel()

          // フォームの入力値を保存する
          // 重複可否モーダルで「登録」を実行した時に使用する
          setTmpValues(input)

          // 重複エラーのフィールド名を取得する
          const duplicateFields = userErrors
            .filter(({ code }) => code === 'DUPLICATE_DATA_EXISTS')
            .flatMap(({ field }) => field)

          // 重複可否モーダルに表示する重複した項目の入力値のオブジェクトを作成する
          const duplicateFieldsValues: DuplicateFieldsValue = {
            zip: input.address.zip,
            city: input.address.city,
            address1: input.address.address1,
            address2: input.address.address2,
          }

          // 重複エラーのフィールド名からエラーメッセージを生成して、ステートを更新
          setDuplicateErrorMessages(
            getDuplicateErrorMessages(duplicateFields, duplicateFieldsValues)
          )
        }
        // 重複エラー以外のエラーがある場合
        else {
          const errorMessages = customerErrorHandling(userErrors)
          onErrorAlert()
          onErrorMessage(errorMessages)
          showMessage(
            'error',
            'エラーが発生しました。入力項目を確認してください。'
          )
        }
      } else if (error) {
        showMessage('error', 'データの更新時にサーバーでエラーが発生しました。')
      } else {
        showMessage('success', 'データの更新が完了しました。')
        onSuccess?.()
      }
    } catch (e) {
      onErrorAlert()
      onErrorMessage([e.toString()])
      showMessage('error', 'データの更新時にサーバーでエラーが発生しました')
    }
  }

  return (
    <>
      <Modal
        title="住所を編集する"
        visible={visible}
        confirmLoading={loading}
        destroyOnClose
        width={700}
        okType="primary"
        okText="保存する"
        onOk={form.submit}
        okButtonProps={{ htmlType: 'submit' }}
        cancelText="キャンセル"
        onCancel={onCancel}
      >
        <Form
          form={form}
          component="form"
          requiredMark="optional"
          onFinish={onFinish}
        >
          <Form.Item name="id" initialValue={defaultValues?.id} hidden>
            <Input />
          </Form.Item>

          <AddressAndDetailsForm form={form} defaultValues={defaultValues} />
        </Form>
      </Modal>

      {/* 重複可否選択モーダル */}
      <AllowDuplicateModal
        errorMessages={duplicateErrorMessages}
        onCancel={() => {
          setShowModal(false)
          setTmpValues(null)
        }}
        onOk={handleClickAllowDuplicate}
        okText="更新"
        open={showModal}
        loading={loading}
      />
    </>
  )
}
